/**
 * Full trade services catalogue
 * Structure: Super-Category → Parent Category → Individual Services
 */

export interface TradeService {
  value: string;
  label: string;
}

export interface TradeParentCategory {
  id: string;
  label: string;
  services: TradeService[];
}

export interface TradeSuperCategory {
  id: string;
  label: string;
  emoji: string;
  categories: TradeParentCategory[];
}

export const TRADE_SUPER_CATEGORIES: TradeSuperCategory[] = [
  {
    id: 'construction',
    label: 'Construction & Property',
    emoji: '🏗️',
    categories: [
      {
        id: 'building-construction',
        label: 'Building & Construction',
        services: [
          { value: 'Artificial Grass Installer', label: 'Artificial Grass Installer' },
          { value: 'Bathroom Fitter', label: 'Bathroom Fitter' },
          { value: 'Bricklayer', label: 'Bricklayer' },
          { value: 'Builder', label: 'Builder' },
          { value: 'Carpenter', label: 'Carpenter' },
          { value: 'Concrete & Screed Layer', label: 'Concrete & Screed Layer' },
          { value: 'Conservatory Specialist', label: 'Conservatory Specialist' },
          { value: 'Damp Proofing Specialist', label: 'Damp Proofing Specialist' },
          { value: 'Extension Specialist', label: 'Extension Specialist' },
          { value: 'Fencer', label: 'Fencer' },
          { value: 'Flooring Specialist', label: 'Flooring Specialist' },
          { value: 'Garage Door Installer', label: 'Garage Door Installer' },
          { value: 'Glazier', label: 'Glazier' },
          { value: 'Handyman', label: 'Handyman' },
          { value: 'Kitchen Fitter', label: 'Kitchen Fitter' },
          { value: 'Loft Conversion Specialist', label: 'Loft Conversion Specialist' },
          { value: 'Plasterer', label: 'Plasterer' },
          { value: 'Roofer', label: 'Roofer' },
          { value: 'Scaffolder', label: 'Scaffolder' },
          { value: 'Stonemason', label: 'Stonemason' },
          { value: 'Tiler', label: 'Tiler' },
        ],
      },
      {
        id: 'finishing-exterior',
        label: 'Finishing & Exterior',
        services: [
          { value: 'Artificial Grass Installer', label: 'Artificial Grass Installer' },
          { value: 'Driveway & Paving Specialist', label: 'Driveway & Paving Specialist' },
          { value: 'Gardener', label: 'Gardener' },
          { value: 'Gutter Cleaner', label: 'Gutter Cleaner / Repair' },
          { value: 'Landscaper', label: 'Landscaper' },
          { value: 'Painter & Decorator', label: 'Painter & Decorator' },
          { value: 'Pest Control', label: 'Pest Control' },
          { value: 'Pressure Washing', label: 'Pressure Washing' },
          { value: 'Render Specialist', label: 'Render Specialist' },
          { value: 'Swimming Pool Maintenance', label: 'Swimming Pool / Hot Tub Maintenance' },
          { value: 'Tree Surgeon', label: 'Tree Surgeon / Arborist' },
        ],
      },
      {
        id: 'mechanical-electrical',
        label: 'Mechanical & Electrical',
        services: [
          { value: 'Appliance Repair', label: 'Appliance Repair' },
          { value: 'Boiler Installer', label: 'Boiler Installer / Repair' },
          { value: 'Drainage Engineer', label: 'Drainage Engineer' },
          { value: 'Electrician', label: 'Electrician' },
          { value: 'EV Charger Installer', label: 'EV Charger Installer' },
          { value: 'Gas Engineer', label: 'Gas Engineer (Gas Safe)' },
          { value: 'Heat Pump Installer', label: 'Heat Pump Installer (MCS)' },
          { value: 'HVAC Engineer', label: 'HVAC Engineer' },
          { value: 'Plumber', label: 'Plumber' },
          { value: 'Smart Home Electrician', label: 'Smart Home Electrician' },
          { value: 'Solar PV Installer', label: 'Solar PV Installer (MCS)' },
          { value: 'Stairlift Installer', label: 'Stairlift Installer' },
          { value: 'Underfloor Heating Specialist', label: 'Underfloor Heating Specialist' },
        ],
      },
      {
        id: 'property-surveying',
        label: 'Property & Surveying',
        services: [
          { value: 'Building Inspector', label: 'Building Inspector' },
          { value: 'Energy Assessor', label: 'Energy Assessor (EPC)' },
          { value: 'Land Surveyor', label: 'Land Surveyor' },
          { value: 'Planning Consultant', label: 'Planning Consultant' },
          { value: 'Property Surveyor', label: 'Property Surveyor' },
          { value: 'Structural Engineer', label: 'Structural Engineer' },
        ],
      },
      {
        id: 'specialist-trades',
        label: 'Specialist Trades',
        services: [
          { value: 'Asbestos Removal', label: 'Asbestos Removal' },
          { value: 'Chimney Sweep', label: 'Chimney Sweep' },
          { value: 'Lift Engineer', label: 'Lift Engineer' },
          { value: 'Steel Erector', label: 'Steel Erector' },
          { value: 'Welder', label: 'Welder / Fabricator' },
        ],
      },
    ],
  },
  {
    id: 'events-hospitality',
    label: 'Events & Hospitality',
    emoji: '🎉',
    categories: [
      {
        id: 'events-hospitality',
        label: 'Events & Hospitality',
        services: [
          { value: 'DJ & Sound Hire', label: 'DJ / Sound Hire' },
          { value: 'Event Caterer', label: 'Event Caterer' },
          { value: 'Marquee Hire', label: 'Marquee Hire' },
          { value: 'Photographer', label: 'Photographer' },
          { value: 'Private Chef', label: 'Private Chef / Meal Prep' },
          { value: 'Wedding Planner', label: 'Wedding Planner' },
        ],
      },
    ],
  },
  {
    id: 'home-lifestyle',
    label: 'Home & Lifestyle',
    emoji: '🏠',
    categories: [
      {
        id: 'cleaning',
        label: 'Cleaning',
        services: [
          { value: 'Carpet Cleaner', label: 'Carpet Cleaner' },
          { value: 'Cleaner', label: 'Cleaner' },
          { value: 'Commercial Cleaner', label: 'Commercial Cleaner' },
          { value: 'End of Tenancy Cleaner', label: 'End of Tenancy Cleaner' },
          { value: 'Ironing & Laundry Service', label: 'Ironing / Laundry Service' },
          { value: 'Oven Cleaner', label: 'Oven Cleaner' },
          { value: 'Sofa & Upholstery Cleaner', label: 'Sofa & Upholstery Cleaner' },
          { value: 'Window Cleaner', label: 'Window Cleaner' },
        ],
      },
      {
        id: 'domestic-lifestyle',
        label: 'Domestic & Lifestyle',
        services: [
          { value: 'Childminder', label: 'Childminder' },
          { value: 'Dog Walker', label: 'Dog Walker' },
          { value: 'Mobile Beautician', label: 'Mobile Beautician' },
          { value: 'Mobile Hairdresser', label: 'Mobile Hairdresser' },
          { value: 'Personal Trainer', label: 'Personal Trainer' },
          { value: 'Pet Groomer', label: 'Pet Groomer' },
        ],
      },
      {
        id: 'interiors-design',
        label: 'Interiors & Design',
        services: [
          { value: 'Architect', label: 'Architect' },
          { value: 'Bespoke Joinery', label: 'Bespoke Joinery' },
          { value: 'Curtain & Blinds Fitter', label: 'Curtain & Blinds Fitter' },
          { value: 'Furniture Assembly', label: 'Furniture Assembly' },
          { value: 'Interior Designer', label: 'Interior Designer' },
          { value: 'TV Mounting', label: 'TV Mounting / AV Installation' },
          { value: 'Wardrobe Specialist', label: 'Wardrobe Specialist' },
        ],
      },
      {
        id: 'removals-logistics',
        label: 'Removals & Logistics',
        services: [
          { value: 'House Removals', label: 'House Removals' },
          { value: 'Man & Van', label: 'Man & Van' },
          { value: 'Office Removals', label: 'Office Removals' },
          { value: 'Piano Movers', label: 'Piano / Specialist Movers' },
          { value: 'Storage Solutions', label: 'Storage Solutions' },
          { value: 'Waste Removal', label: 'Waste Removal / Skip Hire' },
        ],
      },
      {
        id: 'tech-security',
        label: 'Tech & Security',
        services: [
          { value: 'Aerial & Satellite Installer', label: 'Aerial / Satellite Installer' },
          { value: 'Alarm Installer', label: 'Alarm Installer' },
          { value: 'CCTV Installer', label: 'CCTV Installer' },
          { value: 'Fire Alarm Installer', label: 'Fire Alarm / Fire Safety Installer' },
          { value: 'Locksmith', label: 'Locksmith' },
          { value: 'Networking & Wi-Fi Specialist', label: 'Networking / Wi-Fi Specialist' },
        ],
      },
    ],
  },
  {
    id: 'vehicle-transport',
    label: 'Vehicle & Transport',
    emoji: '🚗',
    categories: [
      {
        id: 'vehicle-services',
        label: 'Vehicle Services',
        services: [
          { value: 'Auto Locksmith', label: 'Auto Locksmith / Key Cutting' },
          { value: 'Car Delivery', label: 'Car Delivery' },
          { value: 'Mobile Mechanic', label: 'Mobile Mechanic' },
          { value: 'Mobile Tyre Fitter', label: 'Mobile Tyre Fitter' },
          { value: 'Mobile Valeting', label: 'Mobile Valeting' },
          { value: 'MOT & Servicing', label: 'MOT & Servicing' },
          { value: 'Vehicle Recovery', label: 'Vehicle Recovery' },
          { value: 'Vehicle Wrapping', label: 'Vehicle Wrapping' },
          { value: 'Windscreen Repair', label: 'Windscreen Repair' },
        ],
      },
    ],
  },
];

/** Flat list of all service values (for scraper dropdown) — deduplicated by value */
export const ALL_TRADE_SERVICES: TradeService[] = Array.from(
  new Map(
    TRADE_SUPER_CATEGORIES.flatMap((super_cat) =>
      super_cat.categories.flatMap((cat) => cat.services)
    ).map((s) => [s.value, s])
  ).values()
);

/** Flat list of parent categories (for directory filter) */
export const ALL_PARENT_CATEGORIES: { value: string; label: string; superCategory: string }[] =
  TRADE_SUPER_CATEGORIES.flatMap((super_cat) =>
    super_cat.categories.map((cat) => ({
      value: cat.id,
      label: cat.label,
      superCategory: super_cat.label,
    }))
  );
